import { useRef } from 'react';
import { readingQuestions, readingTypeMeta } from '@/mocks/readingQuestions';
import { readingTypeColorMap } from './typeColors';
import { usePersistentState } from '@/hooks/usePersistentState';
import { TEST_PROGRESS_KEYS } from '@/lib/testProgress';

interface Props {
  onComplete: (answers: Record<string, number>) => void;
}

export default function ReadingTest({ onComplete }: Props) {
  const [progress, setProgress] = usePersistentState<{ current: number; answers: Record<string, number> }>(
    TEST_PROGRESS_KEYS.literacyReading,
    { current: 0, answers: {} },
  );
  const { current, answers } = progress;
  const advancingRef = useRef(false);

  const total = readingQuestions.length;
  const q = readingQuestions[current];
  const selected = q ? (answers[q.id] ?? null) : null;

  if (!q) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 text-center">
          <p className="text-foreground-600 font-semibold">
            검사 문항을 불러오지 못했어요. 다시 시작해주세요.
          </p>
        </div>
      </div>
    );
  }

  const progressPct = (current / total) * 100;
  const isLast = current === total - 1;
  const catMeta = readingTypeMeta.find((c) => c.id === q.type);
  const color = readingTypeColorMap[q.type];

  const handleSelect = (idx: number) => {
    if (advancingRef.current) return;

    const nextAnswers = { ...answers, [q.id]: idx };
    setProgress({ current, answers: nextAnswers });
    advancingRef.current = true;

    window.setTimeout(() => {
      advancingRef.current = false;

      if (isLast) {
        onComplete(nextAnswers);
        return;
      }

      setProgress((p) => ({ current: Math.min(p.current + 1, total - 1), answers: nextAnswers }));
    }, 250);
  };

  const handlePrev = () => {
    if (current > 0) {
      setProgress((p) => ({ current: p.current - 1, answers: p.answers }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${color.badge}`}>
            {catMeta?.label}
          </span>
          <span className="text-sm text-foreground-500">{current + 1} / {total}</span>
        </div>
        <div className="w-full bg-background-200 rounded-full h-2">
          <div
            className={`${color.bar} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 mb-4">
        {/* 지문 */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg flex-shrink-0">
              <span className="text-white text-xs font-bold">{current + 1}</span>
            </div>
            <span className="text-xs font-semibold text-foreground-500 flex items-center gap-1.5">
              <i className="ri-file-text-line text-accent-500"></i> 다음 글을 읽고 물음에 답하세요.
            </span>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-lg px-5 py-4">
            <p className="text-foreground-800 text-sm md:text-base leading-relaxed whitespace-pre-line">
              {q.passage}
            </p>
          </div>
        </div>

        {/* 질문 */}
        <h2 className="font-semibold text-foreground-950 text-base md:text-lg leading-relaxed mb-4">
          {q.question}
        </h2>

        {/* Options */}
        <div className="space-y-3">
          {q.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              className={`whitespace-normal w-full text-left px-4 py-3.5 rounded-xl border text-sm transition-all cursor-pointer ${
                selected === idx
                  ? 'border-primary-400 bg-primary-50 text-primary-800 font-medium'
                  : 'border-background-200 bg-background-50 text-foreground-700 hover:border-background-300 hover:bg-background-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selected === idx ? 'border-primary-500 bg-primary-500' : 'border-background-300'
                }`}>
                  {selected === idx && <i className="ri-check-line text-white text-xs"></i>}
                </div>
                <span className="leading-snug">{opt}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        {current > 0 && (
          <button
            onClick={handlePrev}
            className="whitespace-nowrap px-6 py-3 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>이전
          </button>
        )}
        <span className="text-xs text-foreground-400 ml-auto">답안을 선택하면 자동으로 다음으로 넘어갑니다</span>
      </div>
    </div>
  );
}